/**
 * WP-V2-QC — klient POST /api/quests/complete (PLAN-V2 §7.2).
 * Idempotencja: UUID w body (serwer i tak używa klucza dziennego quest:pid:qid:day).
 */
import {
  QuestCompleteRequestSchema,
  QuestCompleteResponseSchema,
  ApiErrorSchema,
  type QuestCompleteResponse,
} from '@pieskowo/shared';
import { apiBase, authHeaders } from '../core/apiBase.js';

export type QuestCompleteResult =
  | { ok: true; data: QuestCompleteResponse }
  | { ok: false; code: string; retryable: boolean; status: number };

/** uuid v4 do idempotencyKey (crypto.randomUUID lub fallback). */
export function newQuestIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function parseError(res: Response): Promise<QuestCompleteResult> {
  try {
    const json: unknown = await res.json();
    const err = ApiErrorSchema.safeParse(json);
    if (err.success) {
      return {
        ok: false,
        code: err.data.error.code,
        retryable: err.data.error.retryable,
        status: res.status,
      };
    }
  } catch {
    /* ignore */
  }
  return {
    ok: false,
    code: res.status === 401 ? 'UNAUTHORIZED' : res.status === 429 ? 'RATE_LIMITED' : 'INTERNAL',
    retryable: res.status >= 500 || res.status === 429,
    status: res.status,
  };
}

export type CompleteQuestOpts = {
  questId: string;
  /** Jeśli pominięte — generujemy nowy UUID. */
  idempotencyKey?: string;
  /** Inject fetch (testy). */
  fetchImpl?: typeof fetch;
};

/**
 * POST /api/quests/complete { questId, idempotencyKey }.
 * Przy sukcesie zwraca rewardZl + balance (autorytatywne saldo).
 */
export async function completeQuest(
  opts: CompleteQuestOpts,
): Promise<QuestCompleteResult> {
  const idempotencyKey = opts.idempotencyKey ?? newQuestIdempotencyKey();
  const body = { questId: opts.questId, idempotencyKey };
  const parsed = QuestCompleteRequestSchema.safeParse(body);
  if (!parsed.success) {
    return { ok: false, code: 'VALIDATION', retryable: false, status: 0 };
  }

  const doFetch = opts.fetchImpl ?? fetch;
  try {
    const res = await doFetch(`${apiBase()}/api/quests/complete`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (!res.ok) return parseError(res);
    const json: unknown = await res.json();
    const out = QuestCompleteResponseSchema.safeParse(json);
    if (!out.success) {
      return { ok: false, code: 'INTERNAL', retryable: true, status: res.status };
    }
    return { ok: true, data: out.data };
  } catch {
    return { ok: false, code: 'INTERNAL', retryable: true, status: 0 };
  }
}
